'use client';

import './form-email-subscribe.scss';
import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { FormDynamicSettings } from '@sola/schema/lib/schema.ts';
import { setSubscriptionCookie } from '@sola/skippy-sdk/cookies.client.ts';
import { trackLeadFormSuccess } from '@sola/tracking/components/datalayer.tsx';
import { AssetLinksFooter } from '../asset-links.tsx';
import { parseInlineLinks } from '../parse-inline-links.tsx';
interface EmailFormData {
  firstName: string;
  lastName: string;
  email: string;
  subscriptionCode: string;
  isFirstNameRequired?: boolean;
  isLastNameRequired?: boolean;
  subscribeDirectionForm?: 'row' | 'column';
  showInputsPlaceholder?: boolean;
  titleSize?: 'xs' | 'sm' | 'md' | 'lg';
}
export type FormEmailSubscribeProps = {
  className?: string;
  description?: string;
  isFirstNameRequired?: boolean;
  isLastNameRequired?: boolean;
  onSubmit: (data: EmailFormData) => Promise<boolean>;
  config?: Partial<FormDynamicSettings>;
  showInputsPlaceholder?: boolean;
  subscribeDirectionForm?: 'row' | 'column';
  subscriptionCode: string;
  title?: string;
  titleSize?: 'xs' | 'sm' | 'md' | 'lg';
};
const FormEmailSubscribeState = z.object({
  showForm: z.boolean().optional(),
  showThankYou: z.boolean().optional(),
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional()
});
type FormEmailSubscribeState = z.infer<typeof FormEmailSubscribeState>;
export const FormEmailSubscribe: React.FC<FormEmailSubscribeProps> = ({
  subscriptionCode,
  title,
  description,
  className,
  onSubmit,
  config,
  isFirstNameRequired = true,
  isLastNameRequired = true,
  subscribeDirectionForm = 'row',
  showInputsPlaceholder = false,
  titleSize = 'md'
}) => {
  const {
    t
  } = useTranslation(['forms', 'skippy']);
  const [formState, setFormState] = useState<FormEmailSubscribeState>({});
  const labels = {
    firstName: t('forms:contactFields.firstNameLabel'),
    lastName: t('forms:contactFields.lastNameLabel'),
    email: t('forms:contactFields.emailLabel')
  };
  const handleSubmit = async (data: FieldValues) => {
    const {
      firstName,
      lastName,
      email
    } = data;
    let errorMessage = '';
    setFormState(prevState => ({
      ...prevState,
      errorMessage,
      isSubmitting: true
    }));
    try {
      if (await onSubmit({
        firstName,
        lastName,
        email,
        subscriptionCode,
        isFirstNameRequired,
        isLastNameRequired
      })) {
        try {
          setSubscriptionCookie(subscriptionCode);
        } catch {
          console.warn('Failed to set subscription cookie');
        }
        trackLeadFormSuccess(subscriptionCode);
        setFormState({
          showForm: true,
          showThankYou: true,
          isSubmitting: false
        });
        return;
      }
      errorMessage = t('skippy:form.errorVerify');
    } catch {
      errorMessage = t('skippy:form.errorSave');
    }
    if (errorMessage) {
      setFormState(prevState_0 => ({
        ...prevState_0,
        isSubmitting: false,
        errorMessage
      }));
    }
  };
  useEffect(() => {
    setFormState(prevState_1 => ({
      ...prevState_1,
      showForm: true
    }));
  }, [subscriptionCode]);
  const titleSizeClass = {
    xs: 'heading-xs',
    sm: 'heading-sm',
    md: 'heading-md',
    lg: 'heading-lg'
  };
  return <div className={cn('EmailSubscribe max-w-sm', {
    [`${className}`]: !!className
  })}>
      {title && <span className={`block ${titleSizeClass[titleSize]} mb-comfy-md`}>
          {title}
        </span>}
      {description && <span className="block label-lg mb-comfy-md">{description}</span>}
      {formState.showThankYou ? <Alert variant="positive" message={config?.messageThankYou ?? t('forms:emailSubscribe.thankYou')} footer={config?.thankYouAssets && config.thankYouAssets.length > 0 ? <AssetLinksFooter assets={config.thankYouAssets} /> : undefined} /> : <>
          {formState.errorMessage && <div className="mb-comfy-xs">
              <Alert variant="negative" message={formState.errorMessage} />
            </div>}

          <Form onSubmit={handleSubmit} options={{
        mode: 'onTouched'
      }} aria-label={t('forms:emailSubscribe.ariaLabel')}>
            <div className={cn('EmailSubscribe__form grid gap-comfy-lg md:gap-comfy-sm', {
          'grid-cols-1 row': subscribeDirectionForm === 'row',
          'grid-cols-1 column': subscribeDirectionForm === 'column'
        })}>
              <EmailField key="email" label={labels.email} name="email" required placeholder={showInputsPlaceholder ? 'Email Address' : undefined} />
              <InputField key="firstName" label={labels.firstName} name="firstName" autoComplete="given-name" required={isFirstNameRequired} placeholder={showInputsPlaceholder ? 'First Name' : undefined} />
              <InputField key="lastName" label={labels.lastName} name="lastName" autoComplete="family-name" required={isLastNameRequired} placeholder={showInputsPlaceholder ? 'Last Name' : undefined} />
              {config?.consent && <div className="mt-comfy-xs">
                  <Checkbox name="consent" label={config.consentText || t('forms:consentText')} labelContent={config.consentText ? parseInlineLinks(config.consentText) : undefined} required />
                </div>}
              <FormSubmit key="submit" className={cn('EmailSubscribe__button', {
            'mt-comfy-lg': !config?.consent,
            'mt-comfy-xs': !!config?.consent
          })} isBusy={formState.isSubmitting}>
                {config?.buttonText ?? t('forms:emailSubscribe.subscribe')}
              </FormSubmit>
            </div>
          </Form>
        </>}
    </div>;
};